import { useNavigate } from "react-router-dom";
import { CalendarClock } from "lucide-react";
import { useAdmin } from "@/context/AdminContext";
import { useMaintenance } from "@/context/MaintenanceContext";
import { Countdown } from "@/components/maintenance/Countdown";
import { fmtDateTime, tzAbbr, tzCity, viewerTz } from "@/lib/time";

export const COMING_SOON_FORCED = typeof window !== "undefined" && new URLSearchParams(window.location.search).has("coming-soon");

const Headline = ({ text }) => {
  const words = (text || "Our new website is coming soon").trim().split(/\s+/);
  const last = words.pop();
  return (
    <h1 data-testid="coming-soon-heading" className="mt-6 max-w-4xl font-display text-5xl font-extrabold leading-[0.95] tracking-[-0.04em] sm:text-7xl lg:text-8xl">
      {words.join(" ")}{words.length ? " " : ""}<em className="font-serif-accent font-normal italic text-[#E62314]">{last}</em>
    </h1>
  );
};

export const ComingSoon = ({ override, embedded = false }) => {
  const { status, refresh } = useMaintenance();
  const { user, setLoginOpen } = useAdmin();
  const navigate = useNavigate();
  const cs = override || status?.coming_soon || {};
  const tz = viewerTz();
  const launch = cs.launch_at && new Date(cs.launch_at).getTime() > Date.now() ? cs.launch_at : null;

  return (
    <div data-testid="coming-soon-page" className={`grain relative flex flex-col overflow-hidden bg-[#0A0A0A] text-white ${embedded ? "min-h-[600px]" : "min-h-screen"}`}>
      <div aria-hidden className="pointer-events-none absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "repeating-linear-gradient(135deg, #fff 0 1px, transparent 1px 22px)" }} />
      <header className="relative flex items-center justify-between px-5 py-5 sm:px-10">
        <div className="flex items-center gap-3">
          <img src="/assets/logo-dark.png" alt="Cresway Media logo" className="h-11 w-11 object-contain" data-testid="coming-soon-logo" />
          <span className="font-display text-lg font-extrabold tracking-tight">Cresway Media</span>
        </div>
        {!embedded && (
          <button onClick={() => (user ? navigate("/dashboard") : setLoginOpen(true))} data-testid="coming-soon-login"
            className="rounded-full border border-white/30 px-4 py-2 text-sm font-semibold transition-colors hover:bg-white hover:text-black">{user ? "Dashboard" : "Log in"}</button>
        )}
      </header>
      <main className="relative mx-auto flex w-full max-w-7xl flex-1 flex-col justify-center px-5 pb-16 pt-4 sm:px-10">
        <div>
          <p className="flex items-center gap-3 font-mono-label text-xs uppercase tracking-[0.25em] text-zinc-300">
            <span className="inline-flex h-2.5 w-2.5 rounded-full bg-[#E62314]" />
            {launch ? "Launching soon" : "Coming soon"}
          </p>
          <Headline text={cs.title} />
          {cs.message && <p data-testid="coming-soon-message" className="mt-6 max-w-xl text-base leading-relaxed text-zinc-300 sm:text-lg">{cs.message}</p>}
          {launch && (
            <div className="mt-10">
              {cs.show_countdown !== false && <Countdown target={launch} onDone={embedded ? undefined : refresh} testId="coming-soon-countdown" />}
              <p data-testid="coming-soon-launch-at" className="mt-6 flex flex-wrap items-center gap-2 text-sm text-zinc-300 sm:text-base">
                <CalendarClock className="h-4 w-4 text-[#E62314]" />
                Launching <strong className="text-white">{fmtDateTime(launch, tz)}</strong>
                <span className="text-zinc-400">your time · {tzAbbr(launch, tz)} ({tzCity(tz)})</span>
              </p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};
